import type { LatLng } from '@/state/types';

/**
 * Phase 13.7 — Open-water features (rivers, lakes, canals) loaded from OSM
 * via Overpass. Used by the `open-water-pump` module to grant unlimited
 * water output to fire vehicles within 500 m of a feature on a mission.
 *
 * Mirrors the per-city caching pattern from `data/hydrants.ts` and
 * `data/npcStations.ts` (localStorage with center+radius key).
 */

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';

export interface WaterFeature {
  /** Stable id derived from OSM type+id. */
  id: string;
  /** Centroid (for distance check). Linear features get their middle node. */
  position: LatLng;
  /** OSM tag class — "natural=water" or "waterway=*". */
  kind: 'lake' | 'river' | 'canal' | 'stream';
}

interface OverpassNode {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: {
    natural?: string;
    waterway?: string;
    [key: string]: string | undefined;
  };
}

interface OverpassResponse {
  elements: OverpassNode[];
}

function cacheKey(center: LatLng, radiusKm: number): string {
  return `water_features_${center.lat.toFixed(3)}_${center.lng.toFixed(3)}_${radiusKm}`;
}

export async function fetchWaterFeaturesFromOverpass(
  center: LatLng,
  radiusKm: number,
  signal?: AbortSignal,
): Promise<WaterFeature[] | null> {
  const radiusM = Math.round(radiusKm * 1.5 * 1000);
  const query = `[out:json][timeout:25];
(
  node["natural"="water"](around:${radiusM},${center.lat},${center.lng});
  way["natural"="water"](around:${radiusM},${center.lat},${center.lng});
  way["waterway"~"^(river|canal|stream)$"](around:${radiusM},${center.lat},${center.lng});
);
out center body;`;
  try {
    const resp = await fetch(OVERPASS_URL, {
      method: 'POST',
      body: 'data=' + encodeURIComponent(query),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      signal,
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as OverpassResponse;
    if (!Array.isArray(data.elements)) return null;
    return data.elements.map(convert).filter((w): w is WaterFeature => w !== null);
  } catch {
    return null;
  }
}

function convert(el: OverpassNode): WaterFeature | null {
  const lat = el.lat ?? el.center?.lat;
  const lon = el.lon ?? el.center?.lon;
  if (lat === undefined || lon === undefined) return null;
  const tags = el.tags ?? {};
  let kind: WaterFeature['kind'];
  if (tags.natural === 'water') {
    kind = 'lake';
  } else {
    const wt = tags.waterway;
    if (wt === 'river') kind = 'river';
    else if (wt === 'canal') kind = 'canal';
    else if (wt === 'stream') kind = 'stream';
    else return null;
  }
  return {
    id: `osm_water_${el.type}_${el.id}`,
    position: { lat, lng: lon },
    kind,
  };
}

export async function loadWaterFeatures(
  center: LatLng,
  radiusKm: number,
  signal?: AbortSignal,
): Promise<WaterFeature[]> {
  const key = cacheKey(center, radiusKm);
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as WaterFeature[];
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      // ignore
    }
  }
  const live = await fetchWaterFeaturesFromOverpass(center, radiusKm, signal);
  const out = live ?? [];
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(key, JSON.stringify(out));
    } catch {
      // ignore
    }
  }
  return out;
}

/**
 * True if the given point is within `maxDistanceM` of any water feature.
 * Pure helper — no fetching.
 */
export function isNearWater(
  point: LatLng,
  features: ReadonlyArray<WaterFeature>,
  maxDistanceM = 500,
): boolean {
  if (features.length === 0) return false;
  const cosLat = Math.cos((point.lat * Math.PI) / 180);
  const maxKm = maxDistanceM / 1000;
  for (const f of features) {
    const dLat = (f.position.lat - point.lat) * 111.32;
    const dLng = (f.position.lng - point.lng) * 111.32 * cosLat;
    const dKm = Math.sqrt(dLat * dLat + dLng * dLng);
    if (dKm <= maxKm) return true;
  }
  return false;
}
